import { create } from "zustand";
import api from "../lib/api";
import { getTokens, setTokens, clearTokens } from "../lib/tokenStorage";

// Shared session store for BOTH admin and customer sign-in — they hit
// the same /auth/* endpoints, just with different `role` on the
// returned user. Role-gating (e.g. admin-only routes) happens where
// it's actually enforced: AdminGuard on the frontend, requireAdmin on
// the backend — not here.
//
// The tokens themselves live in localStorage (see lib/tokenStorage.js),
// not this store — `user`/`status` here are just the in-memory read of
// "am I logged in," re-derived from a stored token via GET /auth/me on
// app load rather than trusted blindly (a token can be stale/revoked).
const useAuthStore = create((set) => ({
  user: null,
  status: "idle", // idle | loading | authenticated | unauthenticated

  async checkAuth() {
    const { accessToken } = getTokens();
    if (!accessToken) {
      set({ user: null, status: "unauthenticated" });
      return;
    }
    set({ status: "loading" });
    try {
      const { data } = await api.get("/auth/me");
      set({ user: data, status: "authenticated" });
    } catch {
      clearTokens();
      set({ user: null, status: "unauthenticated" });
    }
  },

  async login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    set({ user: data, status: "authenticated" });
    return data;
  },

  async register(payload) {
    const { data } = await api.post("/auth/register", payload);
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    set({ user: data, status: "authenticated" });
    return data;
  },

  async logout() {
    await api.post("/auth/logout").catch(() => {});
    clearTokens();
    set({ user: null, status: "unauthenticated" });
  },
}));

export default useAuthStore;
